export type TaxIdType = Readonly<{
  country: string;
  iso: string;
  code: string;
  description: string;
  example: string;
  impactsTaxCalculation: boolean;
  format?: RegExp;
}>;

export const normalizeTaxIdValue = (value: string): string =>
  value.replace(/\s+/g, "").toUpperCase();

export const findTaxIdTypeByValue = (
  types: ReadonlyArray<TaxIdType>,
  value: string,
): TaxIdType | undefined => types.find((type) => type.format?.test(normalizeTaxIdValue(value)));

export const getTaxIdTypeForCountry = (country: string, value?: string): TaxIdType | null => {
  const types = taxIdTypes.filter((type) => type.iso === country);

  if (types.length === 0) {
    return null;
  }
  if (types.length === 1) {
    return types[0];
  }

  if (value) {
    const matched = findTaxIdTypeByValue(types, value);
    if (matched) {
      return matched;
    }
  }

  return types.find((type) => type.impactsTaxCalculation) ?? types[0];
};

export const taxIdTypes: ReadonlyArray<TaxIdType> = [
  {
    country: "Albania",
    iso: "AL",
    code: "al_tin",
    description: "Albania Tax Identification Number",
    example: "J12345678N",
    impactsTaxCalculation: true,
  },
  {
    country: "Andorra",
    iso: "AD",
    code: "ad_nrt",
    description: "Andorran NRT number",
    example: "A-123456-Z",
    impactsTaxCalculation: false,
  },
  {
    country: "Angola",
    iso: "AO",
    code: "ao_tin",
    description: "Angola Tax Identification Number",
    example: "5123456789",
    impactsTaxCalculation: false,
  },
  {
    country: "Argentina",
    iso: "AR",
    code: "ar_cuit",
    description: "Argentinian tax ID number",
    example: "12-3456789-01",
    impactsTaxCalculation: false,
  },
  {
    country: "Armenia",
    iso: "AM",
    code: "am_tin",
    description: "Armenia Tax Identification Number",
    example: "2538904",
    impactsTaxCalculation: true,
  },
  {
    country: "Aruba",
    iso: "AW",
    code: "aw_tin",
    description: "Aruba Tax Identification Number",
    example: "12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Australia",
    iso: "AU",
    code: "au_abn",
    description: "Australian Business Number (AU ABN)",
    example: "12345678912",
    impactsTaxCalculation: true,
    format: /^[0-9]{11}$/,
  },
  {
    country: "Australia",
    iso: "AU",
    code: "au_arn",
    description: "Australian Taxation Office Reference Number",
    example: "123456789123",
    impactsTaxCalculation: false,
    format: /^[0-9]{12}$/,
  },
  {
    country: "Austria",
    iso: "AT",
    code: "eu_vat",
    description: "European VAT number",
    example: "ATU12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Azerbaijan",
    iso: "AZ",
    code: "az_tin",
    description: "Azerbaijan Tax Identification Number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Bahamas",
    iso: "BS",
    code: "bs_tin",
    description: "Bahamas Tax Identification Number",
    example: "123.456.789",
    impactsTaxCalculation: false,
  },
  {
    country: "Bahrain",
    iso: "BH",
    code: "bh_vat",
    description: "Bahraini VAT Number",
    example: "123456789012345",
    impactsTaxCalculation: true,
  },
  {
    country: "Bangladesh",
    iso: "BD",
    code: "bd_bin",
    description: "Bangladesh Business Identification Number",
    example: "123456789-0123",
    impactsTaxCalculation: true,
  },
  {
    country: "Barbados",
    iso: "BB",
    code: "bb_tin",
    description: "Barbados Tax Identification Number",
    example: "1123456789012",
    impactsTaxCalculation: false,
  },
  {
    country: "Belarus",
    iso: "BY",
    code: "by_tin",
    description: "Belarus TIN Number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Belgium",
    iso: "BE",
    code: "eu_vat",
    description: "European VAT number",
    example: "BE0123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Benin",
    iso: "BJ",
    code: "bj_ifu",
    description: "Benin Tax Identification Number (Identifiant Fiscal Unique)",
    example: "1234567890123",
    impactsTaxCalculation: true,
  },
  {
    country: "Bolivia",
    iso: "BO",
    code: "bo_tin",
    description: "Bolivian tax ID",
    example: "123456789",
    impactsTaxCalculation: false,
  },
  {
    country: "Bosnia & Herzegovina",
    iso: "BA",
    code: "ba_tin",
    description: "Bosnia and Herzegovina Tax Identification Number",
    example: "123456789012",
    impactsTaxCalculation: true,
  },
  {
    country: "Brazil",
    iso: "BR",
    code: "br_cnpj",
    description: "Brazilian CNPJ number",
    example: "01.234.456/5432-10",
    impactsTaxCalculation: false,
    format: /^[0-9]{2}\.?[0-9]{3}\.?[0-9]{3}\/?[0-9]{4}-?[0-9]{2}$/,
  },
  {
    country: "Brazil",
    iso: "BR",
    code: "br_cpf",
    description: "Brazilian CPF number",
    example: "123.456.789-87",
    impactsTaxCalculation: false,
    format: /^[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}-?[0-9]{2}$/,
  },
  {
    country: "Bulgaria",
    iso: "BG",
    code: "bg_uic",
    description: "Bulgaria Unified Identification Code",
    example: "123456789",
    impactsTaxCalculation: false,
    format: /^[0-9]{9}$/,
  },
  {
    country: "Bulgaria",
    iso: "BG",
    code: "eu_vat",
    description: "European VAT number",
    example: "BG0123456789",
    impactsTaxCalculation: true,
    format: /^BG[0-9]{9,10}$/,
  },
  {
    country: "Burkina Faso",
    iso: "BF",
    code: "bf_ifu",
    description: "Burkina Faso Tax Identification Number (Numéro d'Identifiant Fiscal Unique)",
    example: "12345678A",
    impactsTaxCalculation: true,
  },
  {
    country: "Cambodia",
    iso: "KH",
    code: "kh_tin",
    description: "Cambodia Tax Identification Number",
    example: "1001-123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Cameroon",
    iso: "CM",
    code: "cm_niu",
    description: "Cameroon Tax Identification Number (Numéro d'Identifiant fiscal Unique)",
    example: "M123456789000L",
    impactsTaxCalculation: false,
  },
  {
    country: "Canada",
    iso: "CA",
    code: "ca_bn",
    description: "Canadian Business Number",
    example: "123456789",
    impactsTaxCalculation: false,
    format: /^[0-9]{9}$/,
  },
  {
    country: "Canada",
    iso: "CA",
    code: "ca_gst_hst",
    description: "Canadian GST/HST number",
    example: "123456789RT0002",
    impactsTaxCalculation: true,
    format: /^[0-9]{9}RT[0-9]{4}$/,
  },
  {
    country: "Canada",
    iso: "CA",
    code: "ca_pst_bc",
    description: "Canadian PST number (British Columbia)",
    example: "PST-1234-5678",
    impactsTaxCalculation: false,
    format: /^PST-[0-9]{4}-[0-9]{4}$/,
  },
  {
    country: "Canada",
    iso: "CA",
    code: "ca_pst_mb",
    description: "Canadian PST number (Manitoba)",
    example: "123456-7",
    impactsTaxCalculation: false,
    format: /^[0-9]{6}-[0-9]{1}$/,
  },
  {
    country: "Canada",
    iso: "CA",
    code: "ca_pst_sk",
    description: "Canadian PST number (Saskatchewan)",
    example: "1234567",
    impactsTaxCalculation: false,
    format: /^[0-9]{7}$/,
  },
  {
    country: "Canada",
    iso: "CA",
    code: "ca_qst",
    description: "Canadian QST number (Québec)",
    example: "1234567890TQ1234",
    impactsTaxCalculation: true,
    format: /^[0-9]{10}TQ[0-9]{4}$/,
  },
  {
    country: "Cape Verde",
    iso: "CV",
    code: "cv_nif",
    description: "Cape Verde Tax Identification Number (Número de Identificação Fiscal)",
    example: "213456789",
    impactsTaxCalculation: false,
  },
  {
    country: "Chile",
    iso: "CL",
    code: "cl_tin",
    description: "Chilean TIN",
    example: "12.345.678-K",
    impactsTaxCalculation: true,
  },
  {
    country: "China",
    iso: "CN",
    code: "cn_tin",
    description: "Chinese tax ID",
    example: "123456789012345678",
    impactsTaxCalculation: false,
  },
  {
    country: "Colombia",
    iso: "CO",
    code: "co_nit",
    description: "Colombian NIT number",
    example: "123.456.789-0",
    impactsTaxCalculation: false,
  },
  {
    country: "Congo - Kinshasa",
    iso: "CD",
    code: "cd_nif",
    description: "Congo (DR) Tax Identification Number (Número de Identificação Fiscal)",
    example: "A0123456M",
    impactsTaxCalculation: false,
  },
  {
    country: "Costa Rica",
    iso: "CR",
    code: "cr_tin",
    description: "Costa Rican tax ID",
    example: "1-234-567890",
    impactsTaxCalculation: false,
  },
  {
    country: "Croatia",
    iso: "HR",
    code: "eu_vat",
    description: "European VAT number",
    example: "HR12345678912",
    impactsTaxCalculation: true,
    format: /^HR[0-9]{11}$/,
  },
  {
    country: "Croatia",
    iso: "HR",
    code: "hr_oib",
    description: "Croatian Personal Identification Number",
    example: "12345678901",
    impactsTaxCalculation: false,
    format: /^[0-9]{11}$/,
  },
  {
    country: "Cyprus",
    iso: "CY",
    code: "eu_vat",
    description: "European VAT number",
    example: "CY12345678Z",
    impactsTaxCalculation: true,
  },
  {
    country: "Czech Republic",
    iso: "CZ",
    code: "eu_vat",
    description: "European VAT number",
    example: "CZ1234567890",
    impactsTaxCalculation: true,
  },
  {
    country: "Denmark",
    iso: "DK",
    code: "eu_vat",
    description: "European VAT number",
    example: "DK12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Dominican Republic",
    iso: "DO",
    code: "do_rcn",
    description: "Dominican RCN number",
    example: "123-4567890-1",
    impactsTaxCalculation: false,
  },
  {
    country: "Ecuador",
    iso: "EC",
    code: "ec_ruc",
    description: "Ecuadorian RUC number",
    example: "1234567890001",
    impactsTaxCalculation: false,
  },
  {
    country: "Egypt",
    iso: "EG",
    code: "eg_tin",
    description: "Egyptian Tax Identification Number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "El Salvador",
    iso: "SV",
    code: "sv_nit",
    description: "El Salvadorian NIT number",
    example: "1234-567890-123-4",
    impactsTaxCalculation: false,
  },
  {
    country: "Estonia",
    iso: "EE",
    code: "eu_vat",
    description: "European VAT number",
    example: "EE123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Ethiopia",
    iso: "ET",
    code: "et_tin",
    description: "Ethiopia Tax Identification Number",
    example: "1234567890",
    impactsTaxCalculation: true,
  },
  {
    country: "EU",
    iso: "EU",
    code: "eu_oss_vat",
    description: "European One Stop Shop VAT number for non-Union scheme",
    example: "EU123456789",
    impactsTaxCalculation: false,
  },
  {
    country: "Finland",
    iso: "FI",
    code: "eu_vat",
    description: "European VAT number",
    example: "FI12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "France",
    iso: "FR",
    code: "eu_vat",
    description: "European VAT number",
    example: "FRAB123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Georgia",
    iso: "GE",
    code: "ge_vat",
    description: "Georgian VAT",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Germany",
    iso: "DE",
    code: "de_stn",
    description: "German Tax Number (Steuernummer)",
    example: "1234567890",
    impactsTaxCalculation: false,
    format: /^[0-9]{10}$/,
  },
  {
    country: "Germany",
    iso: "DE",
    code: "eu_vat",
    description: "European VAT number",
    example: "DE123456789",
    impactsTaxCalculation: true,
    format: /^DE[0-9]{9}$/,
  },
  {
    country: "Greece",
    iso: "GR",
    code: "eu_vat",
    description: "European VAT number",
    example: "EL123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Guinea",
    iso: "GN",
    code: "gn_nif",
    description: "Guinea Tax Identification Number (Número de Identificação Fiscal)",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Hong Kong",
    iso: "HK",
    code: "hk_br",
    description: "Hong Kong BR number",
    example: "12345678",
    impactsTaxCalculation: false,
  },
  {
    country: "Hungary",
    iso: "HU",
    code: "eu_vat",
    description: "European VAT number",
    example: "HU12345678",
    impactsTaxCalculation: true,
    format: /^HU[0-9]{8}$/,
  },
  {
    country: "Hungary",
    iso: "HU",
    code: "hu_tin",
    description: "Hungary tax number (adószám)",
    example: "12345678-1-23",
    impactsTaxCalculation: false,
    format: /^[0-9]{8}-?[0-9]-?[0-9]{2}$/,
  },
  {
    country: "Iceland",
    iso: "IS",
    code: "is_vat",
    description: "Icelandic VAT",
    example: "123456",
    impactsTaxCalculation: true,
  },
  {
    country: "India",
    iso: "IN",
    code: "in_gst",
    description: "Indian GST number",
    example: "12ABCDE3456FGZH",
    impactsTaxCalculation: true,
  },
  {
    country: "Indonesia",
    iso: "ID",
    code: "id_npwp",
    description: "Indonesian NPWP number",
    example: "012.345.678.9-012.345",
    impactsTaxCalculation: false,
  },
  {
    country: "Ireland",
    iso: "IE",
    code: "eu_vat",
    description: "European VAT number",
    example: "IE1234567AB",
    impactsTaxCalculation: true,
  },
  {
    country: "Israel",
    iso: "IL",
    code: "il_vat",
    description: "Israel VAT",
    example: "12345",
    impactsTaxCalculation: false,
  },
  {
    country: "Italy",
    iso: "IT",
    code: "eu_vat",
    description: "European VAT number",
    example: "IT12345678912",
    impactsTaxCalculation: true,
  },
  {
    country: "Japan",
    iso: "JP",
    code: "jp_cn",
    description: "Japanese Corporate Number (Hōjin Bangō)",
    example: "1234567891234",
    impactsTaxCalculation: false,
    format: /^[0-9]{13}$/,
  },
  {
    country: "Japan",
    iso: "JP",
    code: "jp_rn",
    description:
      "Japanese Registered Foreign Businesses' Registration Number (Tōroku Kokugai Jigyōsha no Tōroku Bangō)",
    example: "12345",
    impactsTaxCalculation: false,
    format: /^[0-9]{5}$/,
  },
  {
    country: "Japan",
    iso: "JP",
    code: "jp_trn",
    description: "Japanese Tax Registration Number (Tōroku Bangō)",
    example: "T1234567891234",
    impactsTaxCalculation: true,
    format: /^T[0-9]{13}$/,
  },
  {
    country: "Kazakhstan",
    iso: "KZ",
    code: "kz_bin",
    description: "Kazakhstani Business Identification Number",
    example: "123456789012",
    impactsTaxCalculation: true,
  },
  {
    country: "Kenya",
    iso: "KE",
    code: "ke_pin",
    description: "Kenya Revenue Authority Personal Identification Number",
    example: "P000111111A",
    impactsTaxCalculation: false,
  },
  {
    country: "Kyrgyzstan",
    iso: "KG",
    code: "kg_tin",
    description: "Kyrgyzstan Tax Identification Number",
    example: "12345678901234",
    impactsTaxCalculation: false,
  },
  {
    country: "Laos",
    iso: "LA",
    code: "la_tin",
    description: "Laos Tax Identification Number",
    example: "123456789-000",
    impactsTaxCalculation: false,
  },
  {
    country: "Latvia",
    iso: "LV",
    code: "eu_vat",
    description: "European VAT number",
    example: "LV12345678912",
    impactsTaxCalculation: true,
  },
  {
    country: "Liechtenstein",
    iso: "LI",
    code: "li_uid",
    description: "Liechtensteinian UID number",
    example: "CHE123456789",
    impactsTaxCalculation: false,
    format: /^CHE[0-9]{9}$/,
  },
  {
    country: "Liechtenstein",
    iso: "LI",
    code: "li_vat",
    description: "Liechtensteinian VAT number",
    example: "12345",
    impactsTaxCalculation: true,
    format: /^[0-9]{5}$/,
  },
  {
    country: "Lithuania",
    iso: "LT",
    code: "eu_vat",
    description: "European VAT number",
    example: "LT123456789123",
    impactsTaxCalculation: true,
  },
  {
    country: "Luxembourg",
    iso: "LU",
    code: "eu_vat",
    description: "European VAT number",
    example: "LU12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Malaysia",
    iso: "MY",
    code: "my_frp",
    description: "Malaysian FRP number",
    example: "12345678",
    impactsTaxCalculation: false,
    format: /^[0-9]{8}$/,
  },
  {
    country: "Malaysia",
    iso: "MY",
    code: "my_itn",
    description: "Malaysian Income Tax Number",
    example: "C 1234567890",
    impactsTaxCalculation: false,
    format: /^[A-Z]{1} ?[0-9]{10}$/,
  },
  {
    country: "Malaysia",
    iso: "MY",
    code: "my_sst",
    description: "Malaysian SST number",
    example: "A12-3456-78912345",
    impactsTaxCalculation: false,
    format: /^[A-Z]{1}[0-9]{2}-?[0-9]{4}-?[0-9]{8}$/,
  },
  {
    country: "Malta",
    iso: "MT",
    code: "eu_vat",
    description: "European VAT number",
    example: "MT12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Mauritania",
    iso: "MR",
    code: "mr_nif",
    description: "Mauritania Tax Identification Number (Número de Identificação Fiscal)",
    example: "12345678",
    impactsTaxCalculation: false,
  },
  {
    country: "Mexico",
    iso: "MX",
    code: "mx_rfc",
    description: "Mexican RFC number",
    example: "ABC010203AB9",
    impactsTaxCalculation: false,
  },
  {
    country: "Moldova",
    iso: "MD",
    code: "md_vat",
    description: "Moldova VAT Number",
    example: "1234567",
    impactsTaxCalculation: true,
  },
  {
    country: "Montenegro",
    iso: "ME",
    code: "me_pib",
    description: "Montenegro PIB Number",
    example: "12345678",
    impactsTaxCalculation: false,
  },
  {
    country: "Morocco",
    iso: "MA",
    code: "ma_vat",
    description: "Morocco VAT Number",
    example: "12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Nepal",
    iso: "NP",
    code: "np_pan",
    description: "Nepal PAN Number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Netherlands",
    iso: "NL",
    code: "eu_vat",
    description: "European VAT number",
    example: "NL123456789B12",
    impactsTaxCalculation: true,
  },
  {
    country: "New Zealand",
    iso: "NZ",
    code: "nz_gst",
    description: "New Zealand GST number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Nigeria",
    iso: "NG",
    code: "ng_tin",
    description: "Nigerian Tax Identification Number",
    example: "12345678-0001",
    impactsTaxCalculation: false,
  },
  {
    country: "North Macedonia",
    iso: "MK",
    code: "mk_vat",
    description: "North Macedonia VAT Number",
    example: "MK1234567890123",
    impactsTaxCalculation: true,
  },
  {
    country: "Norway",
    iso: "NO",
    code: "no_vat",
    description: "Norwegian VAT number",
    example: "123456789MVA",
    impactsTaxCalculation: true,
    format: /^[0-9]{9}MVA$/,
  },
  {
    country: "Norway",
    iso: "NO",
    code: "no_voec",
    description: "Norwegian VAT on e-commerce number",
    example: "1234567",
    impactsTaxCalculation: false,
    format: /^[0-9]{7}$/,
  },
  {
    country: "Oman",
    iso: "OM",
    code: "om_vat",
    description: "Omani VAT Number",
    example: "OM1234567890",
    impactsTaxCalculation: true,
  },
  {
    country: "Peru",
    iso: "PE",
    code: "pe_ruc",
    description: "Peruvian RUC number",
    example: "12345678901",
    impactsTaxCalculation: true,
  },
  {
    country: "Philippines",
    iso: "PH",
    code: "ph_tin",
    description: "Philippines Tax Identification Number",
    example: "123456789012",
    impactsTaxCalculation: true,
  },
  {
    country: "Poland",
    iso: "PL",
    code: "eu_vat",
    description: "European VAT number",
    example: "PL1234567890",
    impactsTaxCalculation: true,
  },
  {
    country: "Portugal",
    iso: "PT",
    code: "eu_vat",
    description: "European VAT number",
    example: "PT123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Romania",
    iso: "RO",
    code: "eu_vat",
    description: "European VAT number",
    example: "RO1234567891",
    impactsTaxCalculation: true,
    format: /^RO[0-9]{2,10}$/,
  },
  {
    country: "Romania",
    iso: "RO",
    code: "ro_tin",
    description: "Romanian tax ID number",
    example: "1234567890123",
    impactsTaxCalculation: false,
    format: /^[0-9]{13}$/,
  },
  {
    country: "Russia",
    iso: "RU",
    code: "ru_inn",
    description: "Russian Taxpayer Identification Number (INN)",
    example: "1234567891",
    impactsTaxCalculation: true,
    format: /^[0-9]{10,12}$/,
  },
  {
    country: "Russia",
    iso: "RU",
    code: "ru_kpp",
    description: "Russian Tax Registration Reason Code (KPP)",
    example: "123456789",
    impactsTaxCalculation: true,
    format: /^[0-9]{9}$/,
  },
  {
    country: "Saudi Arabia",
    iso: "SA",
    code: "sa_vat",
    description: "Saudi Arabia VAT",
    example: "123456789012345",
    impactsTaxCalculation: true,
  },
  {
    country: "Senegal",
    iso: "SN",
    code: "sn_ninea",
    description: "Senegal NINEA Number",
    example: "12345672A2",
    impactsTaxCalculation: false,
  },
  {
    country: "Serbia",
    iso: "RS",
    code: "rs_pib",
    description: "Serbian PIB number",
    example: "123456789",
    impactsTaxCalculation: false,
  },
  {
    country: "Singapore",
    iso: "SG",
    code: "sg_gst",
    description: "Singaporean GST number",
    example: "M12345678X",
    impactsTaxCalculation: true,
    format: /^[A-Z]{1}[0-9]{8}[A-Z]{1}$/,
  },
  {
    country: "Singapore",
    iso: "SG",
    code: "sg_uen",
    description: "Singaporean Unique Entity Number (UEN)",
    example: "123456789F",
    impactsTaxCalculation: false,
    format: /^[0-9]{9}[A-Z]{1}$/,
  },
  {
    country: "Slovakia",
    iso: "SK",
    code: "eu_vat",
    description: "European VAT number",
    example: "SK1234567891",
    impactsTaxCalculation: true,
  },
  {
    country: "Slovenia",
    iso: "SI",
    code: "eu_vat",
    description: "European VAT number",
    example: "SI12345678",
    impactsTaxCalculation: true,
    format: /^SI[0-9]{8}$/,
  },
  {
    country: "Slovenia",
    iso: "SI",
    code: "si_tin",
    description: "Slovenia tax number (davčna številka)",
    example: "12345678",
    impactsTaxCalculation: false,
    format: /^[0-9]{8}$/,
  },
  {
    country: "South Africa",
    iso: "ZA",
    code: "za_vat",
    description: "South African VAT number",
    example: "4123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "South Korea",
    iso: "KR",
    code: "kr_brn",
    description: "Korean BRN",
    example: "123-45-67890",
    impactsTaxCalculation: true,
  },
  {
    country: "Spain",
    iso: "ES",
    code: "es_cif",
    description: "Spanish NIF number (previously Spanish CIF number)",
    example: "A12345678",
    impactsTaxCalculation: false,
    format: /^[A-Z]{1}[0-9]{8}$/,
  },
  {
    country: "Spain",
    iso: "ES",
    code: "eu_vat",
    description: "European VAT number",
    example: "ESA1234567Z",
    impactsTaxCalculation: true,
    format: /^ES[A-Z]{1}[0-9]{7}[A-Z]{1}$/,
  },
  {
    country: "Suriname",
    iso: "SR",
    code: "sr_fin",
    description: "Suriname FIN Number",
    example: "1234567890",
    impactsTaxCalculation: true,
  },
  {
    country: "Sweden",
    iso: "SE",
    code: "eu_vat",
    description: "European VAT number",
    example: "SE123456789123",
    impactsTaxCalculation: true,
  },
  {
    country: "Switzerland",
    iso: "CH",
    code: "ch_uid",
    description: "Switzerland UID number",
    example: "CHE-123.456.789 HR",
    impactsTaxCalculation: false,
    format: /^CHE-?[0-9]{3}\.?[0-9]{3}\.?[0-9]{3} ?HR$/,
  },
  {
    country: "Switzerland",
    iso: "CH",
    code: "ch_vat",
    description: "Switzerland VAT number",
    example: "CHE-123.456.789 MWST",
    impactsTaxCalculation: true,
    format: /^CHE-?[0-9]{3}\.?[0-9]{3}\.?[0-9]{3} ?MWST$/,
  },
  {
    country: "Taiwan",
    iso: "TW",
    code: "tw_vat",
    description: "Taiwanese VAT",
    example: "12345678",
    impactsTaxCalculation: true,
  },
  {
    country: "Tajikistan",
    iso: "TJ",
    code: "tj_tin",
    description: "Tajikistan Tax Identification Number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Tanzania",
    iso: "TZ",
    code: "tz_vat",
    description: "Tanzania VAT Number",
    example: "12345678A",
    impactsTaxCalculation: true,
  },
  {
    country: "Thailand",
    iso: "TH",
    code: "th_vat",
    description: "Thai VAT",
    example: "1234567891234",
    impactsTaxCalculation: true,
  },
  {
    country: "Turkey",
    iso: "TR",
    code: "tr_tin",
    description: "Turkish Tax Identification Number",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "Uganda",
    iso: "UG",
    code: "ug_tin",
    description: "Uganda Tax Identification Number",
    example: "1014751879",
    impactsTaxCalculation: true,
  },
  {
    country: "Ukraine",
    iso: "UA",
    code: "ua_vat",
    description: "Ukrainian VAT",
    example: "123456789",
    impactsTaxCalculation: true,
  },
  {
    country: "United Arab Emirates",
    iso: "AE",
    code: "ae_trn",
    description: "United Arab Emirates TRN",
    example: "123456789012345",
    impactsTaxCalculation: true,
  },
  {
    country: "United Kingdom",
    iso: "GB",
    code: "eu_vat",
    description: "Northern Ireland VAT number",
    example: "XI123456789",
    impactsTaxCalculation: true,
    format: /^XI[0-9]{9}$/,
  },
  {
    country: "United Kingdom",
    iso: "GB",
    code: "gb_vat",
    description: "United Kingdom VAT number",
    example: "GB123456789",
    impactsTaxCalculation: true,
    // Folds the server TaxService's prefixed and unprefixed gb_vat rows so a bare 9-digit value resolves to gb_vat, not the XI eu_vat row.
    format: /^(?:GB)?[0-9]{9}$/,
  },
  {
    country: "United States",
    iso: "US",
    code: "us_ein",
    description: "United States EIN",
    example: "12-3456789",
    impactsTaxCalculation: false,
  },
  {
    country: "Uruguay",
    iso: "UY",
    code: "uy_ruc",
    description: "Uruguayan RUC number",
    example: "123456789012",
    impactsTaxCalculation: true,
  },
  {
    country: "Uzbekistan",
    iso: "UZ",
    code: "uz_tin",
    description: "Uzbekistan TIN Number",
    example: "123456789",
    impactsTaxCalculation: false,
    format: /^[0-9]{9}$/,
  },
  {
    country: "Uzbekistan",
    iso: "UZ",
    code: "uz_vat",
    description: "Uzbekistan VAT Number",
    example: "123456789012",
    impactsTaxCalculation: true,
    format: /^[0-9]{12}$/,
  },
  {
    country: "Venezuela",
    iso: "VE",
    code: "ve_rif",
    description: "Venezuelan RIF number",
    example: "A-12345678-9",
    impactsTaxCalculation: false,
  },
  {
    country: "Vietnam",
    iso: "VN",
    code: "vn_tin",
    description: "Vietnamese tax ID number",
    example: "1234567890",
    impactsTaxCalculation: false,
  },
  {
    country: "Zambia",
    iso: "ZM",
    code: "zm_tin",
    description: "Zambia Tax Identification Number",
    example: "1004751879",
    impactsTaxCalculation: false,
  },
  {
    country: "Zimbabwe",
    iso: "ZW",
    code: "zw_tin",
    description: "Zimbabwe Tax Identification Number",
    example: "1234567890",
    impactsTaxCalculation: false,
  },
];
